/**
 * useDocumentHead — keeps document.title and key meta tags in sync with the
 * current route during client-side navigation.
 *
 * On the FIRST page load, the prerendered HTML already contains the correct
 * head tags for the current route — this hook does nothing visible.
 *
 * When the user navigates client-side (react-router pushState), the prerendered
 * HTML doesn't change. This hook updates document.title, meta description,
 * canonical link, and OG/Twitter tags so the browser tab and any in-page share
 * actions reflect the current route.
 *
 * Server-side crawlers don't need this hook — they fetch each prerendered URL
 * separately and see the correct static <head> in the initial HTML response.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// .mjs imports are enabled via tsconfig allowJs: true
import { getCanonical, getOgImage, getRobots, getSeo } from './seo.mjs';

function setMetaContent(selector: string, value: string) {
    const el = document.querySelector(selector);
    if (el) el.setAttribute('content', value);
}

function setLinkHref(selector: string, value: string) {
    const el = document.querySelector(selector);
    if (el) el.setAttribute('href', value);
}

export function useDocumentHead() {
    const { pathname } = useLocation();

    useEffect(() => {
        const seo = getSeo(pathname);
        if (!seo) return;

        const canonical = getCanonical(pathname);
        const ogImage = getOgImage(pathname);
        const robots = getRobots(pathname);

        document.title = seo.title;

        setMetaContent('meta[name="description"]', seo.description);
        setMetaContent('meta[name="robots"]', robots);
        setLinkHref('link[rel="canonical"]', canonical);

        // Open Graph
        setMetaContent('meta[property="og:url"]', canonical);
        setMetaContent('meta[property="og:title"]', seo.title);
        setMetaContent('meta[property="og:description"]', seo.description);
        setMetaContent('meta[property="og:image"]', ogImage);

        // Twitter
        setMetaContent('meta[name="twitter:title"]', seo.title);
        setMetaContent('meta[name="twitter:description"]', seo.description);
        setMetaContent('meta[name="twitter:image"]', ogImage);
    }, [pathname]);
}
